import { Controller } from "@hotwired/stimulus";

/*
 * Fills in the serial number an evaluation found, never over what the
 * contractor has typed.
 *
 * Polls rather than subscribing: an evaluation can land before a modal opened
 * moments earlier has finished subscribing, and a missed broadcast would leave
 * the spinner up for good.
 */
export default class extends Controller {
  static targets = ["field", "placeholder"];
  static values = { url: String, pending: Boolean, interval: { type: Number, default: 1500 } };

  // Give up after a solid minute of failures rather than retry forever, so a
  // stream that never comes back doesn't leave the tab hammering the endpoint.
  static FAILURE_WINDOW_MS = 60_000;

  connect() {
    this.unmounted = false;
    this.touched = false;
    this.failingSince = null;

    this.#syncPlaceholder();

    if (this.pendingValue) this.#schedule();
  }

  // Flagged as well as cleared: a fetch already in flight resumes after this,
  // and would otherwise reach for targets that are gone and then schedule a
  // fresh timeout on a controller nothing can stop.
  disconnect() {
    this.unmounted = true;
    clearTimeout(this.timeout);
  }

  touch() {
    this.touched = true;
    this.#syncPlaceholder();
  }

  /* Private */

  #schedule() {
    this.timeout = setTimeout(() => this.#tick(), this.intervalValue);
  }

  async #tick() {
    try {
      const response = await fetch(this.urlValue, {
        headers: { Accept: "text/vnd.turbo-stream.html" },
      });

      if (this.unmounted) return;

      if (response.ok) {
        this.failingSince = null;
        const body = await response.text();

        if (this.unmounted) return;

        const evaluation = this.#evaluationStatusFromBody(body);

        // The copy and the serial number come out of one response so they land
        // together. renderStreamMessage applies asynchronously, so reading the
        // serial back off the DOM would take the state from before this render.
        window.Turbo.renderStreamMessage(body);
        this.#fill(evaluation.serial);

        this.pendingValue = evaluation.pending;
        this.#syncPlaceholder();

        if (!this.pendingValue) return;

        this.#schedule();
        return;
      }
    } catch {
      // Nothing worth surfacing: the field is usable regardless, and the next
      // tick may well succeed.
    }

    if (this.unmounted) return;

    this.failingSince ??= Date.now();
    if (Date.now() - this.failingSince >= this.constructor.FAILURE_WINDOW_MS) return;

    this.#schedule();
  }

  // Turbo stream actions carry their content in <template>, whose contents are
  // inert and not reachable from the parsed document - so look inside each one.
  #evaluationStatusFromBody(html) {
    const parsed = new DOMParser().parseFromString(html, "text/html");

    for (const template of parsed.querySelectorAll("template")) {
      const marker = template.content.querySelector("[data-evaluation-pending]");

      if (marker) {
        return {
          pending: marker.dataset.evaluationPending === "true",
          serial: marker.dataset.serialNumber,
        };
      }
    }

    return { pending: false };
  }

  #fill(serial) {
    if (!serial) return;
    if (this.touched || this.fieldTarget.value) return;

    this.fieldTarget.value = serial;
  }

  // A placeholder in behavior as well as looks: gone the moment the field
  // holds anything, back if they clear it while the evaluation still runs.
  #syncPlaceholder() {
    const show = this.pendingValue && this.fieldTarget.value === "";

    this.placeholderTarget.classList.toggle("hidden", !show);
  }
}
